import { useEffect, useState, useCallback } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { fmtMYR, fmtL, fmtDate, prodLabel } from "@/lib/format";
import { DateInput } from "@/components/DateInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table2, Loader2, FileSpreadsheet, RotateCcw } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";
const COLS = [
  ["date", "Date", "text"], ["liters", "Litres", "L"], ["sales", "Sales", "RM"],
  ["delivered", "Delivered", "L"], ["delivery_cost", "Delivery Cost", "RM"],
  ["cash", "Cash", "RM"], ["card", "Card", "RM"], ["qr", "QR Pay", "RM"], ["mart", "Mart", "RM"],
  ["budi_sales", "BUDI Sales", "RM"],
  ["expenses", "Expenses", "RM"], ["bankin", "Bank-in", "RM"], ["variance", "Variance", "RM"],
];

export default function DailyOperationsSheet() {
  const { stationId, setStationId: setStation, stations } = useStation();
  const station = stationId === "all" ? (stations[0]?.id || "") : stationId;
  const [products, setProducts] = useState([]);
  const [product, setProduct] = useState("all");
  const [month, setMonth] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cashOnly, setCashOnly] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/data-months").then((r) => { const m = r.data || []; setMonth(m.length ? m[m.length - 1] : new Date().toISOString().slice(0, 7)); });
  }, []);

  const qs = useCallback(() => {
    const useRange = dateFrom || dateTo;
    const parts = [`station_id=${station}`];
    if (useRange) { if (dateFrom) parts.push(`date_from=${dateFrom}`); if (dateTo) parts.push(`date_to=${dateTo}`); }
    else if (month) parts.push(`month=${month}`);
    if (product !== "all") parts.push(`product=${product}`);
    return parts.join("&");
  }, [station, month, dateFrom, dateTo, product]);

  const load = useCallback(() => {
    if (!station) return;
    setLoading(true);
    api.get(`/daily-operations?${qs()}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [station, qs]);
  useEffect(() => { load(); }, [load]);

  const resetFilters = () => { setProduct("all"); setDateFrom(""); setDateTo(""); setCashOnly(false); };

  const exportXlsx = async () => {
    if (!station) return toast.error("Pick a station");
    setExporting(true);
    try {
      const res = await api.get(`/daily-operations/export?${qs()}`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = u;
      a.download = `daily-operations-${dateFrom || dateTo ? "range" : month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setExporting(false);
  };

  const rows = (data?.rows || []).filter((r) => !cashOnly || (r.cash || r.card || r.mart));
  const t = data?.totals;
  const cell = (r, key, unit) => {
    const v = r[key] ?? 0;
    if (key === "date") return fmtDate(v);
    if (unit === "L") return fmtL(v);
    if (unit === "RM") return fmtMYR(v);
    return v;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Combined ledger</div>
          <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
            <Table2 className="w-8 h-8 text-emerald-400" /> Operations Sheet
          </h1>
          <p className="text-[#9CA3AF] mt-1 text-sm">Every day&apos;s meter, delivery, cash, expenses and banking in one view.</p>
        </div>
        <Button onClick={exportXlsx} disabled={exporting} data-testid="ops-sheet-export" className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold self-start lg:self-end">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />} Download Excel
        </Button>
      </div>

      <div className={`${card} p-4 flex flex-wrap gap-3 items-end`} data-testid="ops-sheet-filters">
        <div>
          <Label className="text-[#9CA3AF] text-xs">Month</Label>
          <Input type="month" data-testid="ops-month-input" value={month} disabled={!!(dateFrom || dateTo)} onChange={(e) => setMonth(e.target.value)} className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40 disabled:opacity-40" />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">Station</Label>
          <Select value={station} onValueChange={setStation}>
            <SelectTrigger data-testid="ops-station-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-48"><SelectValue placeholder="Station" /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#374151] text-white">
              {stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">Product</Label>
          <Select value={product} onValueChange={setProduct}>
            <SelectTrigger data-testid="ops-product-select" className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#111827] border-[#374151] text-white">
              <SelectItem value="all">All Products</SelectItem>
              {products.map((p) => <SelectItem key={p} value={p}>{prodLabel(p)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">From</Label>
          <DateInput data-testid="ops-date-from" value={dateFrom} onChange={setDateFrom} className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40" />
        </div>
        <div>
          <Label className="text-[#9CA3AF] text-xs">To</Label>
          <DateInput data-testid="ops-date-to" value={dateTo} onChange={setDateTo} className="mt-1.5 bg-[#111827] border-[#374151] text-white w-40" />
        </div>
        <label className="flex items-center gap-2 text-sm text-[#9CA3AF] cursor-pointer pb-2" data-testid="ops-cash-only">
          <input type="checkbox" checked={cashOnly} onChange={(e) => setCashOnly(e.target.checked)} className="accent-emerald-500 w-4 h-4" />
          Cash days only
        </label>
        <Button onClick={resetFilters} variant="outline" data-testid="ops-reset-filters" className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">
          <RotateCcw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>
      ) : (
        <div className={`${card} p-5 sm:p-6`}>
          {rows.length ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap" data-testid="ops-sheet-table">
                <thead>
                  <tr className="text-left text-[#9CA3AF] text-xs uppercase font-mono tracking-wider border-b border-[#1F2937]">
                    {COLS.map(([, label]) => <th key={label} className="py-2 pr-4 font-medium">{label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.date} className="border-b border-[#1F2937]/60 hover:bg-white/[0.02]" data-testid={`ops-row-${r.date}`}>
                      {COLS.map(([key, , unit]) => (
                        <td key={key} className={`py-2.5 pr-4 metric-value ${key === "date" ? "text-white/80" : key === "variance" ? (r.variance < 0 ? "text-red-400" : "text-emerald-400") : "text-white/90"}`}>
                          {cell(r, key, unit)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {t && (
                    <tr className="border-t-2 border-[#1F2937] font-bold" data-testid="ops-sheet-total">
                      <td className="py-2.5 pr-4">TOTAL</td>
                      {COLS.slice(1).map(([key, , unit]) => (
                        <td key={key} className={`py-2.5 pr-4 metric-value ${key === "variance" ? (t.variance < 0 ? "text-red-400" : "text-emerald-400") : "text-white"}`}>
                          {unit === "L" ? fmtL(t[key] || 0) : fmtMYR(t[key] || 0)}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : <div className="text-sm text-[#9CA3AF] py-12 text-center" data-testid="ops-sheet-empty">No operations recorded for this selection.</div>}
          <p className="text-xs text-[#9CA3AF] mt-3">Litres &amp; Sales reflect the product filter. Cash, BUDI, expenses &amp; banking are whole-station. Variance = Bank-in − (Cash + Card + Mart + BUDI Sales).</p>
        </div>
      )}
    </div>
  );
}
